import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
   Fade,
   Button,
   Grid,
   Typography,
   TextField,
   FormControl,
   LinearProgress,
   Checkbox,
   FormGroup,
   FormControlLabel,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { makeStyles } from '@mui/styles';
import { OrganizationContext, useMode, UserContext } from '../../lib/contexts';
import { getCookie, setCookie } from '../../lib/helpers/helpers';
import { postUserLogin, postUserLoginConfirm } from '../../lib/api';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles(theme => ({
   container: {
      width: '100%',
      margin: 'auto',
      display: 'block',
      overflowY: 'auto',
   },
   input: {
      '& *': {
         color: 'green',
      },
   },
}));

const LoginForm = ({ onLoginSuccess, variant = 'full' }) => {
   const classes = useStyles();
   const { actions: { setUser } } = useContext(UserContext);
   const { state: { id } } = useContext(OrganizationContext);
   const { mode } = useMode();
   const { enqueueSnackbar } = useSnackbar();
   
   const [status, setStatus] = useState(null);
   const [emailBad, setEmailBad] = useState(false);
   const [tryingLogin, setTryingLogin] = useState(false);
   const [loginEmail, setLoginEmail] = useState('');
   const [OTP, setOTP] = useState('');
   const [phone, setPhone] = useState('');
   // Toggle OTP view vs. normal login view
   const [OTPLogin, setOTPLogin] = useState(false);
   const [rememberMe, setRememberMe] = useState(true);
   const [forgotPassword, setForgotPassword] = useState(false);
   const [resetToken, setResetToken] = useState();
   const [helperText, setHelperText] = useState('');
   const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
   const production = useMemo(() => {
      const host = window.location.hostname;
      return !host.includes('localhost') && !host.includes('staging');
   }, []);
   const signingUp = useMemo(() => window.location.search.includes('signup=true'), []);
   
   useEffect(() => {
      if (signingUp) {
         enqueueSnackbar('Success! Please login with the details used during billing.', {
            variant: 'success',
            autoHideDuration: 8000,
         });
      }
   }, [signingUp, enqueueSnackbar]);
   
   useEffect(() => {
      const callback = event => {
         if (event.code === 'Enter') {
            OTPLogin ? loginRequestConfirm() : loginRequest();
         }
      };
      document.addEventListener('keydown', callback);
      return () => document.removeEventListener('keydown', callback);
   }, [OTP, loginEmail, phone, OTPLogin]);
   
   useEffect(() => {
      if (getCookie('phone')) {
         // Optionally set initial phone from cookie
      }
      if (getCookie('email')) {
         // Optionally set initial email from cookie
      }
   }, []);
   
   useEffect(() => {
      document.title = 'Log In | Carrier Connect Portal';
   }, []);
   
   const validateEmail = useCallback(email => {
      const re =
         /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
   }, []);
   
   const loginRequestConfirm = useCallback(async () => {
      setStatus(null);
      setTryingLogin(true);
      const loginData = {
         login_type: mode,
         email: loginEmail || null,
         phone_number: phone ? '+1' + phone : null,
         token: OTP,
         app: 'Carrier Connect Portal',
      };
      try {
         const { data } = await postUserLoginConfirm(loginData);
         if (data) {
            localStorage.setItem(
               'AUTH',
               JSON.stringify({
                  user: data.user,
                  suggested_organization: data.suggested_organization,
                  organization: data.organization,
                  user_id: data.user.id,
                  token: data.token,
               })
            );
            setUser(data.user);
            if (rememberMe && loginEmail) setCookie('email', loginEmail);
            if (rememberMe && phone) setCookie('phone', phone);
            onLoginSuccess ? onLoginSuccess(data) : window.location.assign('/dashboard');
            setTryingLogin(false);
         } else {
            setStatus('failure');
            setTryingLogin(false);
         }
      } catch (error) {
         console.error(error);
         setStatus('failure');
         setTryingLogin(false);
      }
   }, [mode, loginEmail, phone, OTP, rememberMe, onLoginSuccess, setUser]);
   
   const loginRequest = useCallback(async () => {
      if (loginEmail === 'demo') {
         localStorage.setItem(
            'AUTH',
            JSON.stringify({
               user: { firstName: 'Demo' },
               user_id: -1,
               partner_id: -1,
               token: 'demo',
            })
         );
         window.location.assign('/');
         return;
      }
      setStatus(null);
      setTryingLogin(true);
      const loginData = {
         login_type: mode,
         email: loginEmail || null,
         phone_number: phone ? '+1' + phone : null,
         token: OTP,
         app: 'Carrier Connect Portal',
      };
      try {
         const result = await postUserLogin(loginData, id);
         if (result.status === 204) {
            // Switch to OTP input view
            setOTPLogin(true);
            setTryingLogin(false);
         } else {
            setStatus('failure');
            setTryingLogin(false);
            if (result?.data?.redirect_to) {
               switch (result.data.redirect_to) {
                  case 'billing':
                     window.location.href = production
                        ? 'https://billing.stripe.com/p/login/dRmaEYfdbahlgCkeJOfbq00'
                        : 'https://billing.stripe.com/p/login/test_5kA4hMbCbcxt5iwaEE';
                     break;
                  case 'pricing':
                     window.location.href = 'https://carrierconnector.com/pricing?signup=true';
                     break;
                  default:
                     break;
               }
            }
         }
      } catch (error) {
         console.error(error);
         setStatus('failure');
         setTryingLogin(false);
      }
   }, [mode, production, loginEmail, OTP, phone, id]);
   // Add these useEffect hooks after your existing useEffect hooks

// Clear phone when email is entered
   useEffect(() => {
      if (loginEmail && phone) {
         setPhone('');
      }
   }, [loginEmail]);

// Clear email when phone is entered
   useEffect(() => {
      if (phone && loginEmail) {
         setLoginEmail('');
      }
   }, [phone]);
   return (
      <div className={classes.container}>
         <Fade in={true}>
            <div>
               <Grid container alignItems="center">
                  <Grid item xs>
                     <Fade in={true}>
                        <div>
                           <Typography
                              gutterBottom
                              variant="h5"
                              style={{ marginTop: 20, fontSize: '1rem', textAlign: 'center' }}
                           >
                              {status === 'failure'
                                 ? forgotPassword
                                    ? 'Please double-check your Email address.'
                                    : 'Please double-check your Email and Password.'
                                 : OTPLogin
                                    ? 'Please enter the One-Time-Password sent to your Device:'
                                    : forgotPasswordSuccess
                                       ? "Success! We've sent you an email."
                                       : resetToken
                                          ? 'Please enter new Password and Confirm.'
                                          : 'Please enter your details below:'}
                           </Typography>
                        </div>
                     </Fade>
                  </Grid>
               </Grid>
               {OTPLogin ? (
                  // OTP Login view with OTP text field and Back button
                  <Grid
                     container
                     direction="column"
                     alignItems="center"
                     spacing={2}
                     style={{ width: '100%', margin: 'auto' }}
                  >
                     <Grid item xs={12}>
                        <FormControl style={{ width: 300 }}>
                           <TextField
                              autoFocus
                              error={status === 'failure'}
                              label="Enter One-Time-Password"
                              margin="normal"
                              variant="outlined"
                              fullWidth
                              disabled={tryingLogin}
                              value={OTP}
                              onChange={e => setOTP(e.target.value)}
                              type="text"
                              InputProps={{ classes: { input: classes.input } }}
                           />
                        </FormControl>
                     </Grid>
                     <Grid item xs={12} style={{ textAlign: 'center' }}>
                        <Button variant="text" onClick={() => setOTPLogin(false)}>
                           Back
                        </Button>
                     </Grid>
                     <Grid item xs={12}>
                        {tryingLogin ? (
                           <LinearProgress style={{ width: '80%', margin: '20px auto' }} />
                        ) : (
                           <Button
                              variant="contained"
                              color="primary"
                              style={{ width: 300, marginBottom: 20, marginTop: 0 }}
                              onClick={loginRequestConfirm}
                           >
                              Sign In &nbsp;
                              <LockIcon />
                           </Button>
                        )}
                     </Grid>
                  </Grid>
               ) : (
                  // Normal login view with Email and Phone fields
                  <Grid
                     container
                     direction="column"
                     alignItems="center"
                     spacing={2}
                     style={{ width: '100%', margin: 'auto' }}
                  >
                     <Grid item xs={12}>
                        <FormControl style={{ width: 300 }}>
                           <TextField
                              autoComplete="email"
                              error={status === 'failure' || emailBad}
                              required
                              label={
                                 mode === 'admin'
                                    ? 'Enter Work Email'
                                    : mode === 'track'
                                       ? 'Enter Tracking ID'
                                       : 'Enter Email'
                              }
                              margin="normal"
                              variant="outlined"
                              fullWidth
                              disabled={tryingLogin}
                              value={loginEmail}
                              onChange={e => {
                                 setLoginEmail(e.target.value);
                                 setEmailBad(!validateEmail(e.target.value));
                                 // Clear phone if email is being entered
                                 if (e.target.value && phone) {
                                    setPhone('');
                                 }
                                 
                              }}
                              type="email"
                              helperText={helperText}
                              InputProps={{ classes: { input: classes.input } }}
                           />
                        </FormControl>
                        <Typography style={{ marginTop: 8, textAlign: 'center' }}>OR</Typography>
                        <FormControl style={{ width: 300 }}>
                           <TextField
                              value={phone}
                              onChange={e => {
                                 const val = e.target.value.replace(/[^0-9]/g, '');
                                 if (val.length < 11) setPhone(val);
                                 
                                 // Clear email if phone is being entered
                                 if (val && loginEmail) {
                                    setLoginEmail('');
                                 }
                                 
                              }}
                              error={status === 'failure'}
                              required={!loginEmail}
                              label="Enter Phone Number"
                              margin="normal"
                              variant="outlined"
                              fullWidth
                              disabled={tryingLogin}
                              type="text"
                              InputProps={{ max: 10, classes: { input: classes.input } }}
                           />
                        </FormControl>
                     </Grid>
                     {tryingLogin ? (
                        <LinearProgress style={{ width: '80%', margin: '20px auto' }} />
                     ) : (
                        <Button
                           variant="contained"
                           color="primary"
                           style={{ width: 300, marginBottom: 20, marginTop: 0 }}
                           onClick={loginRequest}
                        >
                           Sign In &nbsp;
                           <LockIcon />
                        </Button>
                     )}
                     <Grid item xs={12}>
                        <FormGroup>
                           <FormControlLabel
                              control={
                                 <Checkbox
                                    checked={rememberMe}
                                    onChange={() => setRememberMe(!rememberMe)}
                                 />
                              }
                              label="Remember Me?"
                           />
                        </FormGroup>
                     </Grid>
                  </Grid>
               )}
            </div>
         </Fade>
      </div>
   );
};

export default LoginForm;
